const { format, isValid, parse } = require('date-fns');
const { enUS } = require('date-fns/locale');

const getLocaleCode = (locale) => {
    switch (locale) {
        case 'fr-CA':
            return 'fr-CA';
        case 'es-MX':
            return 'es';
        case 'en-US':
        default:
            return 'en-US';
    }
};

const resolveLocaleModule = (localeModule) => {
    return localeModule?.default || localeModule || enUS;
};

const getValidDate = (value) => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    try {
        const date = value instanceof Date ? value : new Date(value);
        return isValid(date) ? date : undefined;
    } catch {
        return undefined;
    }
};

const safeFormatDate = (value, locale = enUS) => {
    const date = getValidDate(value);
    if (!date) {
        return '';
    }

    try {
        return format(date, 'P', { locale });
    } catch {
        return '';
    }
};

const safeParseDate = (value, locale = enUS) => {
    if (typeof value !== 'string' || value.trim() === '') {
        return undefined;
    }

    try {
        const date = parse(value, 'P', new Date(), { locale });
        return isValid(date) ? date : undefined;
    } catch {
        return undefined;
    }
};

const toDayTime = (value) => {
    const date = getValidDate(value);
    if (!date) {
        return undefined;
    }

    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const isSameDay = (left, right) => {
    const leftTime = toDayTime(left);
    const rightTime = toDayTime(right);
    return leftTime !== undefined && rightTime !== undefined && leftTime === rightTime;
};

const isWithinRange = (date, from, to) => {
    const dateTime = toDayTime(date);
    const fromTime = toDayTime(from);
    const toTime = toDayTime(to);

    if (dateTime === undefined) {
        return false;
    }

    if (fromTime !== undefined && dateTime < fromTime) {
        return false;
    }

    if (toTime !== undefined && dateTime > toTime) {
        return false;
    }

    return fromTime !== undefined || toTime !== undefined;
};

const isBeforeDay = (date, before) => {
    const dateTime = toDayTime(date);
    const beforeTime = toDayTime(before);
    return dateTime !== undefined && beforeTime !== undefined && dateTime < beforeTime;
};

const isAfterDay = (date, after) => {
    const dateTime = toDayTime(date);
    const afterTime = toDayTime(after);
    return dateTime !== undefined && afterTime !== undefined && dateTime > afterTime;
};

const matchesDisabledMatcher = (date, matcher) => {
    if (matcher === true) {
        return true;
    }

    if (!matcher) {
        return false;
    }

    if (Array.isArray(matcher)) {
        return matcher.some(item => matchesDisabledMatcher(date, item));
    }

    if (matcher instanceof Date) {
        return isSameDay(date, matcher);
    }

    if (typeof matcher === 'function') {
        try {
            return matcher(date) === true;
        } catch {
            return false;
        }
    }

    if (typeof matcher !== 'object') {
        return false;
    }

    if (Array.isArray(matcher.dayOfWeek)) {
        return matcher.dayOfWeek.includes(date.getDay());
    }

    if (matcher.from !== undefined || matcher.to !== undefined) {
        return isWithinRange(date, matcher.from, matcher.to);
    }

    if (matcher.before !== undefined || matcher.after !== undefined) {
        return isBeforeDay(date, matcher.before) || isAfterDay(date, matcher.after);
    }

    return false;
};

const isDisabledDate = (date, disabled) => {
    const validDate = getValidDate(date);
    if (!validDate) {
        return false;
    }

    return matchesDisabledMatcher(validDate, disabled);
};

module.exports = {
    getLocaleCode,
    getValidDate,
    isDisabledDate,
    resolveLocaleModule,
    safeFormatDate,
    safeParseDate
};
